/**
 * Camera video-file derivation from an episode's hardware block.
 *
 * One implementation shared by the episode detail view, dataset curation,
 * and the review-queue prefetch. The prefetch and the consuming hook must
 * derive byte-identical lists, or the presigned-URL react-query key
 * misses and the prefetch is wasted.
 *
 * Precedence:
 *   1. `hardware.cameras` — ordered list of video basenames written by
 *      runtime metadata schema 1.1 (e.g. "left_camera_rgb").
 *   2. Legacy dict-shaped `hardware.camera_serials` ({serial: role} or
 *      {role: serial} — both shapes exist in the wild; prefer whichever
 *      side mentions "camera").
 *   3. The mark0 default triple.
 *
 * Results are sorted alphabetically; nothing is ever dropped.
 */

export const DEFAULT_CAMERA_FILES = [
  "left_camera_rgb.mp4",
  "center_camera_rgb.mp4",
  "right_camera_rgb.mp4",
];

export type CameraPosition = "left" | "center" | "right";

/** Camera identity: the video-file basename without extension. */
export function cameraNameForFile(file: string): string {
  return file.split("/").pop()?.replace(/\.[^.]+$/, "") ?? file;
}

/** Episode frames per frame of a fast rendition (see `fastRenditionFile`). */
export const FAST_RENDITION_FACTOR = 8;
export const FAST_RENDITION_MANIFEST = "review/manifest.json";

/**
 * Fast rendition of a camera file, keeping every factor-th frame,
 * stored next to the episode under `review/`. The presign endpoint signs any
 * name without checking the object, so episodes without one get a URL that
 * fails to load, and the player keeps 8x on seek mode.
 */
export function fastRenditionFile(cameraFile: string, factor: 8 | 16 = FAST_RENDITION_FACTOR): string {
  return `review/${cameraNameForFile(cameraFile)}_${factor}x.mp4`;
}

export interface ReviewRenditionManifest {
  version: 1;
  cameras: string[];
  factors: [8, 16];
}

/** Completion marker written only after both copy sets upload. */
export function parseReviewRenditionManifest(value: unknown): ReviewRenditionManifest | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Record<string, unknown>;
  if (candidate.version !== 1 || !Array.isArray(candidate.cameras) || !candidate.cameras.length) return null;
  const cameras = candidate.cameras;
  if (!cameras.every((camera): camera is string =>
    typeof camera === "string" && /^[A-Za-z0-9][A-Za-z0-9_.-]*\.mp4$/.test(camera),
  )) return null;
  const sortedCameras = [...cameras].sort();
  if (new Set(cameras).size !== cameras.length || cameras.some((camera, i) => camera !== sortedCameras[i])) return null;
  if (!Array.isArray(candidate.factors) || candidate.factors.length !== 2 || candidate.factors[0] !== 8 || candidate.factors[1] !== 16) return null;
  return { version: 1, cameras, factors: [8, 16] };
}

export function has32xReviewCopies(manifest: ReviewRenditionManifest | null | undefined, cameraFiles: readonly string[]): boolean {
  return Boolean(manifest && cameraFiles.length && cameraFiles.every((file) => manifest.cameras.includes(file)));
}

/**
 * Every file the episode view presigns: cameras, their fast renditions, then
 * audio. The review-queue prefetch builds the same list, so both hit one
 * presigned-URL query key.
 */
export function episodeMediaFiles(
  cameraFiles: string[],
  { hasAudio, hasTranscript }: { hasAudio?: boolean | null; hasTranscript?: boolean | null },
): string[] {
  const files = [
    ...cameraFiles,
    ...cameraFiles.map((file) => fastRenditionFile(file)),
    ...cameraFiles.map((file) => fastRenditionFile(file, 16)),
    FAST_RENDITION_MANIFEST,
  ];
  if (hasAudio) files.push("audio.mp3");
  if (hasTranscript) files.push("audio.vtt");
  return files;
}

/**
 * Name classifier used only to resolve the legacy "left"/"center"/
 * "right" selection tokens still living in persisted state and shared
 * URLs. Display order is alphabetical and does not use this.
 */
export function cameraPositionForName(name: string): CameraPosition | null {
  const lower = name.toLowerCase();
  if (lower.includes("depth")) return null;
  const firstToken = lower.split(/[_\-.]/)[0];
  if (firstToken === "left" || firstToken === "center" || firstToken === "right") {
    return firstToken;
  }
  if (lower.includes("wrist")) return "left";
  if (lower.includes("centre")) return "center";
  if (lower.includes("overhead")) return "right";
  return null;
}

function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((entry) => typeof entry === "string" && entry.length > 0)
  );
}

/** Derive the full camera video-file list from episode.hardware. */
export function deriveCameraFiles(hardware: unknown): string[] {
  let files: string[] | null = null;
  if (hardware && typeof hardware === "object") {
    const hw = hardware as Record<string, unknown>;
    if (isStringArray(hw.cameras)) {
      files = hw.cameras.map((entry) =>
        entry.endsWith(".mp4") ? entry : `${entry}.mp4`,
      );
    } else {
      const serials = hw.camera_serials;
      if (serials && typeof serials === "object" && !Array.isArray(serials)) {
        const keys = Object.keys(serials as Record<string, unknown>);
        const values = Object.values(serials as Record<string, unknown>).filter(
          (value): value is string => typeof value === "string",
        );
        // Role names contain "camera"; serial numbers don't. Prefer the
        // side that looks like role names (webapp historically used keys,
        // thumbnails used values — this reconciles both shapes).
        const roles = keys.some((key) => key.toLowerCase().includes("camera"))
          ? keys
          : values.some((value) => value.toLowerCase().includes("camera"))
            ? values
            : keys;
        if (roles.length > 0) {
          files = roles.map((name) => `${name}.mp4`);
        }
      }
    }
  }
  const result = files ?? [...DEFAULT_CAMERA_FILES];
  return result.slice().sort();
}

function isPositionToken(value: string): value is CameraPosition {
  return value === "left" || value === "center" || value === "right";
}

/**
 * Resolve visibleCameras entries onto camera names. Exact name match
 * wins; a legacy position token ("left"/"center"/"right") takes the
 * FIRST camera at that position — the single source of truth for token
 * semantics, shared by the player, the config popover, and theater mode
 * so a token can never select more cameras than the player renders.
 * Empty selection defaults to center.
 *
 * The selection is a set: results come back in cameraNames order
 * (alphabetical), never in click or URL order. No zero-match fallback
 * here; callers that must show something (the player) add their own.
 */
export function resolveVisibleCameraNames(
  cameraNames: string[],
  visibleCameras: string[],
): string[] {
  const visible = visibleCameras.length > 0 ? visibleCameras : ["center"];
  const matched = new Set<string>();
  for (const entry of visible) {
    const byName = cameraNames.find((name) => name === entry);
    const match =
      byName ??
      (isPositionToken(entry)
        ? cameraNames.find((name) => cameraPositionForName(name) === entry)
        : undefined);
    if (match !== undefined) matched.add(match);
  }
  return cameraNames.filter((name) => matched.has(name));
}

/**
 * Resolve visibleCameras entries against the full file list via
 * resolveVisibleCameraNames. Zero matches fall back to the first file
 * (preserves the pre-N-camera behavior: the player always shows one).
 */
export function resolveVisibleCameraFiles(
  allFiles: string[],
  visibleCameras: string[],
): string[] {
  const names = allFiles.map(cameraNameForFile);
  const selected = resolveVisibleCameraNames(names, visibleCameras).map(
    (name) => allFiles[names.indexOf(name)],
  );
  return selected.length > 0 ? selected : allFiles.slice(0, 1);
}
